/**
 * ECOSMITH Navigation Component
 * Floating pill navbar with glassmorphism + mega-menu dropdowns
 */

class EcoNav extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.isScrolled = false;
    this.isMobileMenuOpen = false;
    this.activeDropdown = null;
    this._closeDropdownTimer = null;
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
    this.initScrollBehavior();
    this.setActiveLinkFromUrl();
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          --nav-height: 72px;
          display: block;
          position: fixed;
          top: 16px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 1000;
          width: auto;
          transition: top 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        :host(.scrolled) { top: 8px; }

        /* ── Pill container ── */
        .nav-container {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: rgba(250, 251, 252, 0.88);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(192, 197, 206, 0.45);
          border-radius: 9999px;
          box-shadow: 0 4px 24px rgba(10,14,20,.08), 0 1px 2px rgba(10,14,20,.04);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
        }
        :host(.scrolled) .nav-container {
          padding: 6px 10px;
          background: rgba(250, 251, 252, 0.97);
          box-shadow: 0 8px 32px rgba(10,14,20,.12), 0 2px 4px rgba(10,14,20,.06);
        }

        /* ── Logo ── */
        .logo {
          display: flex;
          align-items: center;
          padding: 4px 12px;
          text-decoration: none;
          border-radius: 9999px;
          transition: background 0.2s;
          flex-shrink: 0;
        }
        .logo:hover { background: rgba(42,75,42,.05); }
        .logo img { height: 40px; width: auto; display: block; object-fit: contain; }

        /* ── Desktop nav items ── */
        .desktop-nav { display: flex; align-items: center; gap: 4px; }
        .nav-links { display: flex; align-items: center; gap: 4px; list-style: none; margin: 0; padding: 0; }

        /* ── Nav item wrapper (for dropdown positioning) ── */
        .nav-item {
          position: static;
        }

        .nav-link {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 10px 16px;
          color: #4A5568;
          text-decoration: none;
          font-size: 0.875rem;
          font-weight: 500;
          border-radius: 9999px;
          transition: all 0.2s;
          cursor: pointer;
          border: none;
          background: transparent;
          font-family: inherit;
          white-space: nowrap;
        }
        .nav-link:hover, .nav-link.hovered { color: #2A4B2A; background: rgba(42,75,42,.06); }
        .nav-link.active { color: #638A58; background: rgba(148,181,138,.1); }

        .nav-link .chevron {
          width: 12px; height: 12px;
          transition: transform 0.25s;
          opacity: 0.6;
          flex-shrink: 0;
        }
        .nav-link.hovered .chevron { transform: rotate(180deg); opacity: 1; }

        /* ── CTA ── */
        .cta-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          background: linear-gradient(135deg, #94B58A 0%, #638A58 100%);
          color: white;
          text-decoration: none;
          font-size: 0.875rem;
          font-weight: 600;
          border-radius: 9999px;
          border: none;
          cursor: pointer;
          transition: all 0.2s;
          font-family: inherit;
          box-shadow: 0 2px 8px rgba(148,181,138,.3);
          white-space: nowrap;
        }
        .cta-button:hover { transform: translateY(-1px); box-shadow: 0 4px 16px rgba(148,181,138,.4); }
        .cta-button svg { width: 15px; height: 15px; }

        /* ── Mega-menu panel ── */
        .mega-menu {
          position: fixed;
          top: calc(var(--panel-top, 80px));
          left: 50%;
          transform: translateX(-50%) translateY(-8px);
          min-width: 520px;
          background: rgba(250, 251, 252, 0.96);
          backdrop-filter: blur(28px) saturate(1.8);
          -webkit-backdrop-filter: blur(28px) saturate(1.8);
          border: 1px solid rgba(192, 197, 206, 0.5);
          border-radius: 20px;
          padding: 24px;
          box-shadow:
            0 0 0 1px rgba(255,255,255,0.8) inset,
            0 20px 60px rgba(10,14,20,0.12),
            0 6px 20px rgba(10,14,20,0.07);
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.2s, transform 0.2s;
          z-index: 1001;
        }
        .mega-menu.open {
          opacity: 1;
          pointer-events: auto;
          transform: translateX(-50%) translateY(0);
        }

        .mega-header {
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #638A58;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid rgba(0,0,0,0.07);
        }

        .mega-grid {
          display: grid;
          gap: 6px;
        }
        .mega-grid.cols-2 { grid-template-columns: 1fr 1fr; }
        .mega-grid.cols-3 { grid-template-columns: 1fr 1fr 1fr; }

        .mega-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 14px;
          border-radius: 12px;
          text-decoration: none;
          cursor: pointer;
          transition: background 0.2s;
          border: 1px solid transparent;
        }
        .mega-item:hover {
          background: rgba(42,75,42,0.05);
          border-color: rgba(42,75,42,0.12);
        }

        .mega-item-icon {
          width: 38px;
          height: 38px;
          border-radius: 10px;
          background: rgba(100,160,90,0.1);
          box-shadow: 0 1px 0 rgba(255,255,255,0.8) inset;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #638A58;
          flex-shrink: 0;
        }
        .mega-item-icon svg { width: 18px; height: 18px; }

        .mega-item-text { flex: 1; min-width: 0; }
        .mega-item-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: #1a2e1a;
          margin-bottom: 3px;
          white-space: nowrap;
        }
        .mega-item-desc {
          font-size: 0.75rem;
          color: #718096;
          line-height: 1.4;
        }

        .mega-footer {
          margin-top: 16px;
          padding-top: 14px;
          border-top: 1px solid rgba(0,0,0,0.07);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .mega-footer-link {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.8rem;
          color: #638A58;
          cursor: pointer;
          font-weight: 600;
          text-decoration: none;
          transition: gap 0.2s;
        }
        .mega-footer-link:hover { gap: 10px; }
        .mega-footer-link svg { width: 14px; height: 14px; }

        /* ── Mobile menu button ── */
        .mobile-menu-btn {
          display: none;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          padding: 0;
          background: transparent;
          border: none;
          border-radius: 9999px;
          cursor: pointer;
          color: #2A4B2A;
          transition: background 0.2s;
        }
        .mobile-menu-btn:hover { background: rgba(42,75,42,.06); }
        .mobile-menu-btn svg { width: 24px; height: 24px; }

        /* ── Mobile panel ── */
        .mobile-panel {
          display: none;
          position: fixed;
          top: 80px;
          left: 16px;
          right: 16px;
          background: linear-gradient(160deg,
            rgba(88, 101, 118, 0.88) 0%,
            rgba(42, 52, 65, 0.96) 100%);
          backdrop-filter: blur(32px) saturate(1.6);
          -webkit-backdrop-filter: blur(32px) saturate(1.6);
          border: 1px solid rgba(255,255,255,0.18);
          border-bottom-color: rgba(255,255,255,0.06);
          border-radius: 20px;
          padding: 16px;
          box-shadow:
            0 0 0 1px rgba(255,255,255,0.06) inset,
            0 1px 0 rgba(255,255,255,0.18) inset,
            0 24px 48px rgba(0,0,0,0.35);
          opacity: 0;
          transform: translateY(-10px) scale(0.98);
          pointer-events: none;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          max-height: calc(100vh - 110px);
          overflow-y: auto;
        }
        .mobile-panel.open {
          opacity: 1;
          transform: translateY(0) scale(1);
          pointer-events: auto;
        }

        .mobile-section { margin-bottom: 8px; }
        .mobile-section-label {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 14px;
          color: rgba(255,255,255,0.88);
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          border-radius: 12px;
          transition: background 0.2s;
        }
        .mobile-section-label { color: rgba(255,255,255,0.88); }
        .mobile-section-label:hover { background: rgba(255,255,255,0.08); }
        .mobile-section-label.open { color: #94B58A; }

        .mobile-sub-items { display: none; padding: 4px 8px 8px; }
        .mobile-sub-items.open { display: block; }
        .mobile-sub-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          border-radius: 10px;
          cursor: pointer;
          text-decoration: none;
          transition: background 0.2s;
          color: rgba(255,255,255,0.6);
          font-size: 0.85rem;
        }
        .mobile-sub-item:hover { background: rgba(255,255,255,0.05); color: rgba(255,255,255,0.9); }
        .mobile-sub-item-icon {
          width: 30px; height: 30px;
          border-radius: 8px;
          background: rgba(148,181,138,0.1);
          display: flex; align-items: center; justify-content: center;
          color: #94B58A; flex-shrink: 0;
        }
        .mobile-sub-item-icon svg { width: 14px; height: 14px; }

        .mobile-cta {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 14px;
          margin-top: 8px;
          background: linear-gradient(135deg, #94B58A 0%, #638A58 100%);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 0.9rem;
          font-weight: 600;
          font-family: inherit;
          cursor: pointer;
          transition: opacity 0.2s;
          text-decoration: none;
        }
        .mobile-cta:hover { opacity: 0.9; }

        /* ── Responsive ── */
        @media (max-width: 900px) {
          .desktop-nav, .cta-button { display: none; }
          .mobile-menu-btn { display: flex; }
          .mobile-panel { display: block; }
        }
        @media (max-width: 480px) {
          .logo img { height: 32px; }
        }
      </style>

      <nav class="nav-container" role="navigation" aria-label="Main navigation">
        <a href="/" class="logo" aria-label="ECOSMITH Home">
          <img src="/logo.png" alt="ECOSMITH Pakistan" />
        </a>

        <div class="desktop-nav">
          <ul class="nav-links">

            <!-- Products -->
            <li class="nav-item" data-menu="products">
              <button class="nav-link" data-href="/products">
                Products
                <svg class="chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 6l4 4 4-4"/></svg>
              </button>
            </li>

            <!-- Process -->
            <li class="nav-item" data-menu="process">
              <button class="nav-link" data-href="/process">
                Process
                <svg class="chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 6l4 4 4-4"/></svg>
              </button>
            </li>

            <!-- Resources -->
            <li class="nav-item" data-menu="resources">
              <button class="nav-link" data-href="/resources">
                Resources
                <svg class="chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 6l4 4 4-4"/></svg>
              </button>
            </li>

            <!-- Careers (direct link) -->
            <li class="nav-item">
              <button class="nav-link" data-href="/careers">Careers</button>
            </li>

          </ul>
        </div>

        <a class="cta-button" href="/contact">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
          Contact Us
        </a>

        <button class="mobile-menu-btn" aria-label="Toggle menu" aria-expanded="false">
          <svg class="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          <svg class="close-icon" style="display:none" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </nav>

      <!-- Products Mega Menu -->
      <div class="mega-menu" id="mega-products" role="dialog" aria-label="Products menu">
        <div class="mega-header">Product Lines</div>
        <div class="mega-grid cols-2">
          <a class="mega-item" href="/products#cip">
            <div class="mega-item-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg>
            </div>
            <div class="mega-item-text">
              <div class="mega-item-title">CIP / SIP Systems</div>
              <div class="mega-item-desc">Automated clean-in-place and sterilise-in-place skids</div>
            </div>
          </a>
          <a class="mega-item" href="/products#tanks">
            <div class="mega-item-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="4" y="3" width="16" height="18" rx="3"/><path d="M8 10h8M8 14h5"/></svg>
            </div>
            <div class="mega-item-text">
              <div class="mega-item-title">Mixing & Storage Tanks</div>
              <div class="mega-item-desc">SS 304 / 316L hygienic vessels with agitators</div>
            </div>
          </a>
          <a class="mega-item" href="/products#heat-exchangers">
            <div class="mega-item-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 12h4l3-6 4 12 3-6h4"/></svg>
            </div>
            <div class="mega-item-text">
              <div class="mega-item-title">Heat Exchangers</div>
              <div class="mega-item-desc">Plate and tubular type for pasteurisation</div>
            </div>
          </a>
          <a class="mega-item" href="/products#conveyors">
            <div class="mega-item-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 17l16-10M4 7l16 10"/><circle cx="4" cy="12" r="2"/><circle cx="20" cy="12" r="2"/></svg>
            </div>
            <div class="mega-item-text">
              <div class="mega-item-title">Conveyors & Lifts</div>
              <div class="mega-item-desc">Hygienic belt and chain conveying systems</div>
            </div>
          </a>
        </div>
        <div class="mega-footer">
          <a class="mega-footer-link" href="/products">
            View all products
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M3 8h10M9 4l4 4-4 4"/></svg>
          </a>
        </div>
      </div>

      <!-- Process Mega Menu -->
      <div class="mega-menu" id="mega-process" role="dialog" aria-label="Process menu">
        <div class="mega-header">Engineering Process</div>
        <div class="mega-grid cols-3">
          <a class="mega-item" href="/process#requirements">
            <div class="mega-item-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M7 9h10M7 13h6"/></svg>
            </div>
            <div class="mega-item-text">
              <div class="mega-item-title">01 · Scoping</div>
              <div class="mega-item-desc">Requirements & HACCP analysis</div>
            </div>
          </a>
          <a class="mega-item" href="/process#concept">
            <div class="mega-item-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="9"/><path d="M12 8v4l3 2"/></svg>
            </div>
            <div class="mega-item-text">
              <div class="mega-item-title">02 · Concept</div>
              <div class="mega-item-desc">3D layout & P&ID design</div>
            </div>
          </a>
          <a class="mega-item" href="/process#engineering">
            <div class="mega-item-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
            </div>
            <div class="mega-item-text">
              <div class="mega-item-title">03 · Engineering</div>
              <div class="mega-item-desc">Fabrication drawings & weld specs</div>
            </div>
          </a>
          <a class="mega-item" href="/process#fabrication">
            <div class="mega-item-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>
            </div>
            <div class="mega-item-text">
              <div class="mega-item-title">04 · Fabrication</div>
              <div class="mega-item-desc">Orbital TIG welding & QA</div>
            </div>
          </a>
          <a class="mega-item" href="/process#fat">
            <div class="mega-item-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M9 12l2 2 4-4"/><rect x="3" y="3" width="18" height="18" rx="3"/></svg>
            </div>
            <div class="mega-item-text">
              <div class="mega-item-title">05 · FAT</div>
              <div class="mega-item-desc">Factory acceptance testing</div>
            </div>
          </a>
          <a class="mega-item" href="/process#commissioning">
            <div class="mega-item-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M5 3l14 9-14 9V3z"/></svg>
            </div>
            <div class="mega-item-text">
              <div class="mega-item-title">06 · Commissioning</div>
              <div class="mega-item-desc">Site installation & training</div>
            </div>
          </a>
        </div>
        <div class="mega-footer">
          <a class="mega-footer-link" href="/process">
            Full process overview
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M3 8h10M9 4l4 4-4 4"/></svg>
          </a>
        </div>
      </div>

      <!-- Resources Mega Menu -->
      <div class="mega-menu" id="mega-resources" role="dialog" aria-label="Resources menu">
        <div class="mega-header">Resources Library</div>
        <div class="mega-grid cols-2">
          <a class="mega-item" href="/resources#whitepapers">
            <div class="mega-item-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
            </div>
            <div class="mega-item-text">
              <div class="mega-item-title">Whitepapers</div>
              <div class="mega-item-desc">Technical guides on CIP design & EHEDG compliance</div>
            </div>
          </a>
          <a class="mega-item" href="/resources#datasheets">
            <div class="mega-item-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
            </div>
            <div class="mega-item-text">
              <div class="mega-item-title">Datasheets</div>
              <div class="mega-item-desc">Product specifications and material certifications</div>
            </div>
          </a>
          <a class="mega-item" href="/resources#case-studies">
            <div class="mega-item-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
            </div>
            <div class="mega-item-text">
              <div class="mega-item-title">Case Studies</div>
              <div class="mega-item-desc">Real-world installations across Pakistan's food sector</div>
            </div>
          </a>
          <a class="mega-item" href="/resources#videos">
            <div class="mega-item-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
            </div>
            <div class="mega-item-text">
              <div class="mega-item-title">Videos</div>
              <div class="mega-item-desc">Installation walkthroughs and CIP cycle demos</div>
            </div>
          </a>
        </div>
        <div class="mega-footer">
          <a class="mega-footer-link" href="/resources">
            Browse all resources
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M3 8h10M9 4l4 4-4 4"/></svg>
          </a>
        </div>
      </div>

      <!-- Mobile panel -->
      <div class="mobile-panel" id="mobile-panel" role="menu">
        <!-- Products -->
        <div class="mobile-section">
          <div class="mobile-section-label" data-mobile-toggle="mobile-products">
            Products
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 6l4 4 4-4"/></svg>
          </div>
          <div class="mobile-sub-items" id="mobile-products">
            <a class="mobile-sub-item" href="/products#cip">
              <div class="mobile-sub-item-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg></div>
              CIP / SIP Systems
            </a>
            <a class="mobile-sub-item" href="/products#tanks">
              <div class="mobile-sub-item-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="4" y="3" width="16" height="18" rx="3"/></svg></div>
              Mixing &amp; Storage Tanks
            </a>
            <a class="mobile-sub-item" href="/products#heat-exchangers">
              <div class="mobile-sub-item-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 12h4l3-6 4 12 3-6h4"/></svg></div>
              Heat Exchangers
            </a>
            <a class="mobile-sub-item" href="/products#conveyors">
              <div class="mobile-sub-item-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 17l16-10M4 7l16 10"/></svg></div>
              Conveyors &amp; Lifts
            </a>
          </div>
        </div>

        <!-- Process -->
        <div class="mobile-section">
          <div class="mobile-section-label" data-mobile-toggle="mobile-process">
            Process
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 6l4 4 4-4"/></svg>
          </div>
          <div class="mobile-sub-items" id="mobile-process">
            <a class="mobile-sub-item" href="/process#requirements">01 · Scoping &amp; Requirements</a>
            <a class="mobile-sub-item" href="/process#concept">02 · Concept Design</a>
            <a class="mobile-sub-item" href="/process#fabrication">03–04 · Engineering &amp; Fabrication</a>
            <a class="mobile-sub-item" href="/process#fat">05–06 · FAT &amp; Commissioning</a>
          </div>
        </div>

        <!-- Resources -->
        <div class="mobile-section">
          <div class="mobile-section-label" data-mobile-toggle="mobile-resources">
            Resources
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 6l4 4 4-4"/></svg>
          </div>
          <div class="mobile-sub-items" id="mobile-resources">
            <a class="mobile-sub-item" href="/resources#whitepapers">Whitepapers</a>
            <a class="mobile-sub-item" href="/resources#datasheets">Datasheets</a>
            <a class="mobile-sub-item" href="/resources#case-studies">Case Studies</a>
            <a class="mobile-sub-item" href="/resources#videos">Videos</a>
          </div>
        </div>

        <!-- Careers direct link -->
        <div class="mobile-section">
          <a class="mobile-section-label" href="/careers" style="text-decoration:none; display:flex;">
            Careers
          </a>
        </div>

        <a class="mobile-cta" href="/contact">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
          Contact Us
        </a>
      </div>
    `;
  }

  attachEventListeners() {
    // ── Desktop: hover-based mega menus ──
    const menuItems = this.shadowRoot.querySelectorAll('.nav-item[data-menu]');
    menuItems.forEach(item => {
      const menuId = item.dataset.menu;
      const btn = item.querySelector('.nav-link');
      const panel = this.shadowRoot.getElementById(`mega-${menuId}`);
      if (!panel) return;

      const open = () => {
        clearTimeout(this._closeDropdownTimer);
        // Close any other open dropdown
        if (this.activeDropdown && this.activeDropdown !== panel) {
          this._closePanel(this.activeDropdown);
        }
        this.activeDropdown = panel;
        btn.classList.add('hovered');
        panel.classList.add('open');
        // Position relative to nav bar bottom
        const navRect = this.shadowRoot.querySelector('.nav-container').getBoundingClientRect();
        this.style.setProperty('--panel-top', `${navRect.bottom + 8}px`);
      };

      const scheduleClose = () => {
        this._closeDropdownTimer = setTimeout(() => {
          btn.classList.remove('hovered');
          panel.classList.remove('open');
          if (this.activeDropdown === panel) this.activeDropdown = null;
        }, 120);
      };

      item.addEventListener('mouseenter', open);
      item.addEventListener('mouseleave', scheduleClose);
      panel.addEventListener('mouseenter', () => clearTimeout(this._closeDropdownTimer));
      panel.addEventListener('mouseleave', scheduleClose);

      // Click navigates to the section page
      btn.addEventListener('click', () => {
        window.location.href = btn.dataset.href || `/${menuId}`;
      });
    });

    // Direct links (no dropdown)
    this.shadowRoot.querySelectorAll('.nav-item:not([data-menu]) .nav-link').forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.dataset.href) window.location.href = btn.dataset.href;
      });
    });

    // Close dropdown on outside click
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target) && this.activeDropdown) {
        this._closePanel(this.activeDropdown);
      }
    });

    // ── Mobile: toggle button ──
    const mobileBtn = this.shadowRoot.querySelector('.mobile-menu-btn');
    const mobilePanel = this.shadowRoot.getElementById('mobile-panel');
    mobileBtn.addEventListener('click', () => {
      this.isMobileMenuOpen = !this.isMobileMenuOpen;
      mobilePanel.classList.toggle('open', this.isMobileMenuOpen);
      this.shadowRoot.querySelector('.menu-icon').style.display = this.isMobileMenuOpen ? 'none' : 'block';
      this.shadowRoot.querySelector('.close-icon').style.display = this.isMobileMenuOpen ? 'block' : 'none';
      mobileBtn.setAttribute('aria-expanded', this.isMobileMenuOpen);
    });

    // ── Mobile: accordion toggles ──
    this.shadowRoot.querySelectorAll('[data-mobile-toggle]').forEach(label => {
      label.addEventListener('click', () => {
        const targetId = label.dataset.mobileToggle;
        const sub = this.shadowRoot.getElementById(targetId);
        const isOpen = sub.classList.toggle('open');
        label.classList.toggle('open', isOpen);
      });
    });

    // Close mobile panel on outside click
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target) && this.isMobileMenuOpen) {
        this.isMobileMenuOpen = false;
        mobilePanel.classList.remove('open');
      }
    });
  }

  _closePanel(panel) {
    panel.classList.remove('open');
    const menuId = panel.id.replace('mega-', '');
    const btn = this.shadowRoot.querySelector(`.nav-item[data-menu="${menuId}"] .nav-link`);
    if (btn) btn.classList.remove('hovered');
    if (this.activeDropdown === panel) this.activeDropdown = null;
  }

  initScrollBehavior() {
    let ticking = false;
    const update = () => {
      const scrolled = window.scrollY > 50;
      if (scrolled !== this.isScrolled) {
        this.isScrolled = scrolled;
        this.classList.toggle('scrolled', scrolled);
      }
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
  }

  setActiveLinkFromUrl() {
    const path = window.location.pathname;
    this.shadowRoot.querySelectorAll('.nav-link').forEach(link => {
      const href = link.dataset.href || '';
      // Active if current path starts with the nav href (but don't mark / as always active)
      link.classList.toggle('active', href && href !== '/' && path.startsWith(href.split('#')[0]));
    });
  }
}

customElements.define('eco-nav', EcoNav);